/**
 * Industry Library — shared pure helpers + constants.
 *
 * Pulled out of IndustryLibraryPage.jsx during the 2026-02-19 split so
 * the page entry, editor modal, quick-fix modal, and drill-down sheet
 * can import these without circular-dep risk.
 */
import {
  Factory, HeartPulse, Landmark, Cpu, ShoppingBag, Briefcase, Building, Zap, Layers,
  ArrowUpRight, ArrowDownRight, Minus,
} from 'lucide-react';

export const API = process.env.REACT_APP_BACKEND_URL;

// Icon catalogue mirrored from IndustryModelPanel so the cards render
// the same glyph everywhere.
export const ICON_MAP = {
  Factory, HeartPulse, Landmark, Cpu, ShoppingBag, Briefcase, Building, Zap, Layers,
};
export const ICON_CHOICES = Object.keys(ICON_MAP);

export function slugify(s) {
  return (s || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function formatCompactDollar(n) {
  if (!n || n === 0) return '$0';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(0)}k`;
  return `$${Math.round(n).toLocaleString()}`;
}

export function fmtCompact(n) {
  if (n == null) return '—';
  const abs = Math.abs(n);
  if (abs >= 1000) return `${(n / 1000).toFixed(1)}k`;
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2);
}

/**
 * Map a 0–100 health score to a visual tier. <40 = red (needs
 * attention), 40–69 = amber (ok), 70+ = emerald (healthy). Returns
 * the tailwind class set + a short label for the chip text.
 */
export function healthTier(score) {
  if (score == null) return null;
  if (score >= 70) {
    return {
      label: 'Healthy',
      classes: 'bg-emerald-50 border-emerald-200 text-emerald-700',
      dot: 'bg-emerald-500',
    };
  }
  if (score >= 40) {
    return {
      label: 'Building',
      classes: 'bg-amber-50 border-amber-200 text-amber-700',
      dot: 'bg-amber-500',
    };
  }
  return {
    label: 'Needs attention',
    classes: 'bg-rose-50 border-rose-200 text-rose-700',
    dot: 'bg-rose-500',
  };
}

/**
 * Visualise a 7-day score delta. Green ↑ when climbing, red ↓ when
 * decaying, neutral · when flat. Returns null for industries we don't
 * yet have a baseline snapshot for (suppresses the chip rather than
 * lying about a 0 delta).
 */
export function trendDisplay(trend) {
  if (!trend || typeof trend.delta !== 'number') return null;
  const d = trend.delta;
  if (d > 0) {
    return {
      icon: ArrowUpRight,
      label: `+${d}`,
      classes: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    };
  }
  if (d < 0) {
    return {
      icon: ArrowDownRight,
      label: `${d}`,
      classes: 'bg-rose-50 border-rose-200 text-rose-700',
    };
  }
  return {
    icon: Minus,
    label: '0',
    classes: 'bg-slate-50 border-slate-200 text-slate-600',
  };
}

export const emptyDraft = () => ({
  id: '',
  name: '',
  short_label: '',
  description: '',
  icon: 'Layers',
  kpi_priorities: [],
  kpi_baselines: [],
  lei_kpi_weights: [],
  elasticities: [],
  portfolio_dampening_factor: '',
  max_kpi_uplift_percent: '',
  llm_industry_context: '',
  published: true,
});

/**
 * Lightweight stats for peer industries — used to render
 * "Others set this to $X–$Y (avg $Z, N profiles)" hints under each
 * editor input so admins can sanity-check against the library before
 * saving.
 *
 * Skips the current draft + archived profiles + missing values so the
 * hint reflects the *active baseline* admins should compare against.
 */
export function peerStats(peerProfiles, draftId, extract) {
  const vals = [];
  for (const p of peerProfiles) {
    if (!p || p.id === draftId) continue;
    if (p.published === false) continue;
    const v = extract(p);
    if (v == null || v === '' || Number.isNaN(Number(v))) continue;
    vals.push(Number(v));
  }
  if (vals.length === 0) return null;
  vals.sort((a, b) => a - b);
  const min = vals[0];
  const max = vals[vals.length - 1];
  const avg = vals.reduce((s, x) => s + x, 0) / vals.length;
  return { min, max, avg, n: vals.length };
}
